import * as React from 'react';
import axios, { AxiosProgressEvent } from 'axios';

import { Button } from '@independent-software/typeui/controls/Button'
import { Icon } from '@independent-software/typeui/controls/Icon'
import { Dialog } from '@independent-software/typeui/controls/Dialog'
import { Dropzone } from '@independent-software/typeui/controls/Dropzone'
import { Message } from '@independent-software/typeui/controls/Message'
import { Divider } from '@independent-software/typeui/controls/Divider'
import { Progress } from '@independent-software/typeui/controls/Progress'
import { IAuth } from '../../AuthContext';

interface IProps {
  className?: string;
  children?: React.ReactNode;
  open: boolean;
  url: string;
  title: React.ReactNode;
  onClose: () => void;
  onSuccess: () => void;
  auth: IAuth;
}

type TStep = 'input' | 'uploading' | 'error' | 'success';

const Upload = (props: IProps) => {
  const [step, setStep] = React.useState('input' as TStep);
  const [progress, setProgress] = React.useState(0);
  const [response, setResponse] = React.useState(null);

 const handleClose = () => {
    // Reinitialize state. This is given a small timeout
    // so that state update isn't seen by user while dialog
    // is closing.
    setTimeout(() => setStep('input'), 300);
    // Close the dialog.
    props.onClose();
  }

  const handleUploadProgress = (e: AxiosProgressEvent) => {
    setProgress(Math.round(e.loaded * 100 / e.total));
  }

  const handleAddFiles = (files: File[]) => {
    const file: File = files[0];
    const config = { onUploadProgress: handleUploadProgress };
    const data = new FormData();
    data.append('access_token', props.auth.access_token);
    data.append('file', file);
    axios.post(props.url, data, config)
      .then((res) => {
        props.onSuccess();
        setResponse(res.data);
        setStep('success');
      })
      .catch((err) => {
        let response = "";
        if(err.response) {
          response = err.response.data;
        } else if (err.request) {
          response = "Error building server request.";
        } else {
          response = err.message;
        }
        setResponse(response);
        setStep('error');
      });

    setStep('uploading');
  }

  return (
    <Dialog open={props.open}>
      <Dialog.Header>{props.title}</Dialog.Header>
      <Dialog.Content>
        {step == 'input' && <Dropzone onAddFiles={handleAddFiles}/>}
        {step != 'input' && 
          <Progress value={progress} numbered/>}
        {step == 'uploading' && 
          <React.Fragment>
            <Divider hidden/>
            <Message type="info" icon>
              <Icon loading name="circle-notch" size="big"/>              
              <Message.Content>
                <Message.Header>Just a moment...</Message.Header>
                Uploading and processing.
              </Message.Content>
            </Message>
          </React.Fragment>}
        {step == 'error' &&
          <React.Fragment>
            <Divider hidden/>
            <Message type="error">
              <Message.Header>The server returned an error message</Message.Header>
              {response}
            </Message>
          </React.Fragment>}
        {step == 'success' && 
          <React.Fragment>
            <Divider hidden/>
            <Message type="success">
              <Message.Header>Upload successful.</Message.Header>
              {response}
            </Message>
          </React.Fragment>}          
      </Dialog.Content>
      <Dialog.Footer>
        <Button secondary onClick={handleClose}>
          {step == 'success' ? "OK" : "Cancel"}
        </Button>
      </Dialog.Footer>
    </Dialog>
  );
}

export { Upload };
